import { useEffect, useState } from "react";
import { Plug, Wifi, Loader2, Settings2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { CredencialDialog } from "@/components/integraciones/CredencialDialog";
import { listResource, api } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Tarjetas con adaptador real conmutable por entidad
const CARD_PROVEEDOR = {
  "CONDUSEF REUNE": "condusef",
  "CONDUSEF REDECO": "condusef",
  "Círculo de Crédito": "circulo",
  "Buró de Crédito": "buro_pf",
  "Buró de Crédito Empresas": "buro_pm",
  "PUI / RENAPO": "pui",
};

export default function Integraciones() {
  const { user } = useAuth();
  const puedeConfig = ["admin_entidad", "tecnico"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [testing, setTesting] = useState(null);
  const [cfgProv, setCfgProv] = useState(null);

  const load = () => listResource("integraciones").then(setItems);
  useEffect(() => { load(); }, []);

  const probar = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/integraciones/${id}/probar`);
      toast.success(data.estado);
      load();
    } catch (e) {
      toast.error("No se pudo probar la conexión");
    } finally {
      setTesting(null);
    }
  };

  return (
    <div>
      <PageHeader title="Centro de Integraciones" subtitle="Conexiones con autoridades y buró — adaptador real conmutable por entidad (mock · sandbox · producción)" />
      <div className="p-6 md:p-8">
        <div className="mb-5 rounded-md border border-sage/40 bg-sage/15 px-4 py-2.5 text-sm text-forest-600">
          Las tarjetas de CONDUSEF, Buró y Círculo cuentan con adaptador real. En <strong>modo demo (mock)</strong> operan simuladas;
          carga las credenciales institucionales de tu entidad y cambia a <strong>sandbox/producción</strong> para conectar de verdad.
          FELVORA orquesta; la relación con la autoridad o el buró corresponde al sujeto obligado.
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((i) => {
            const prov = CARD_PROVEEDOR[i.nombre];
            return (
              <div key={i.id} data-testid={`integracion-${i.id}`} className="flex flex-col rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Plug className="h-5 w-5 text-primary" /></div>
                  <StatusBadge value={i.estatus} />
                </div>
                <p className="mt-3 font-heading text-sm font-semibold text-slate-800">{i.nombre}</p>
                <p className="text-xs text-slate-400">Ambiente: {i.ambiente} · {i.categoria}{prov ? " · Adaptador real" : ""}</p>
                <div className="mt-3 flex-1 space-y-1 text-xs text-slate-500">
                  <p>Última conexión: {i.ultima_conexion ? fdatetime(i.ultima_conexion) : "—"}</p>
                  {i.ultimo_resultado && <p className="text-emerald-600">{i.ultimo_resultado}</p>}
                </div>
                {prov ? (
                  <Button data-testid={`configurar-${prov}`} variant="outline" size="sm" className="mt-4 w-full"
                    onClick={() => setCfgProv(prov)} disabled={!puedeConfig}>
                    <Settings2 className="mr-1.5 h-4 w-4" /> {puedeConfig ? "Configurar conexión" : "Solo admin / técnico"}
                  </Button>
                ) : (
                  <Button data-testid={`probar-${i.id}`} variant="outline" size="sm" className="mt-4 w-full"
                    onClick={() => probar(i.id)} disabled={testing === i.id}>
                    {testing === i.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Wifi className="mr-1.5 h-4 w-4" />} Probar conexión
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <CredencialDialog proveedor={cfgProv} open={!!cfgProv} onOpenChange={(o) => !o && setCfgProv(null)} onSaved={load} />
    </div>
  );
}
